import { getMouseVisual } from '../assets/mice/MouseVisualRegistry';

export const DARMOSHARK_VENDOR_ID = 0x1915;
export const DARMOSHARK_M9_PRODUCT_ID = 0x0803;
export const DARMOSHARK_M6_ULTRA_PRODUCT_ID = 0x0812;
export const DARMOSHARK_M2_SE_PRODUCT_ID = 0x0806;
export const DARMOSHARK_54L_M3_ULTRA_PRODUCT_ID = 0x0807;
export const DARMOSHARK_M5_V2_PRODUCT_ID = 0x0808;
export const DARMOSHARK_M3_MICRO_V2_PRODUCT_ID = 0x0809;
export const MS502_V3_PRODUCT_ID = 0x9001;
export const DARMOSHARK_RECEIVER_PRODUCT_ID = 0x0801;
export const LM20_DONGLE_RECEIVER_PRODUCT_ID = 0x9000;
export const WANI_8K_RECEIVER_PRODUCT_ID = 0xf001;
export const WANI_G1_PRODUCT_ID = 0xf002;
export const WANI_H2_PRODUCT_ID = 0xf003;

const LEGACY_DPI_COLOR_MODEL_IDS = new Set([
  'darmoshark-m9',
  'darmoshark-m6-ultra',
]);

export function vidPidKey(vendorId, productId) {
  return `${Number(vendorId)}:${Number(productId)}`;
}

/**
 * 型号表。image 取自视觉注册表；尚未提供外观资源的型号 image 为 null，
 * 设备卡片会退回首字母占位，按键分配页仍按 MouseVisualRegistry 的兜底几何渲染。
 */
const MODEL_DEFINITIONS = [
  {
    id: 'darmoshark-m9',
    name: 'DarmoShark M9',
    productId: DARMOSHARK_M9_PRODUCT_ID,
  },
  {
    id: 'darmoshark-m6-ultra',
    name: 'DarmoShark M6 Ultra',
    productId: DARMOSHARK_M6_ULTRA_PRODUCT_ID,
  },
  {
    id: 'darmoshark-m2-se',
    name: 'DarmoShark M2 SE',
    productId: DARMOSHARK_M2_SE_PRODUCT_ID,
  },
  {
    id: 'darmoshark-54l-m3-ultra',
    name: 'DarmoShark 54L M3 Ultra',
    productId: DARMOSHARK_54L_M3_ULTRA_PRODUCT_ID,
  },
  {
    id: 'darmoshark-m5-v2',
    name: 'DarmoShark M5 V2',
    productId: DARMOSHARK_M5_V2_PRODUCT_ID,
  },
  {
    id: 'darmoshark-m3-micro-v2',
    name: 'DarmoShark M3 Micro V2',
    productId: DARMOSHARK_M3_MICRO_V2_PRODUCT_ID,
  },
  {
    // LM20 平台，只走 4.0 协议，支持 HITS。
    // 有线名 MS805 8K Mouse；蓝牙连接名与蓝牙配对名都是 MS805。
    id: 'ms502-v3',
    name: 'MS805 8K Mouse',
    bluetoothName: 'MS805',
    bluetoothPairingName: 'MS805',
    productId: MS502_V3_PRODUCT_ID,
  },
  {
    id: 'wani-g1',
    name: 'G1 Gaming Mouse',
    bluetoothName: 'wani G1',
    productId: WANI_G1_PRODUCT_ID,
  },
  {
    id: 'wani-h2',
    name: 'H2 Gaming Mouse',
    bluetoothName: 'wani H2',
    productId: WANI_H2_PRODUCT_ID,
  },
];

const DEVICE_MODELS = new Map(
  MODEL_DEFINITIONS.map(
    ({ id, name, productId, bluetoothName = null, bluetoothPairingName = null }) => [
      vidPidKey(DARMOSHARK_VENDOR_ID, productId),
      Object.freeze({
        id,
        name,
        bluetoothName,
        bluetoothPairingName,
        vendorId: DARMOSHARK_VENDOR_ID,
        productId,
        image: getMouseVisual(id)?.image || null,
      }),
    ]
  )
);

const RECEIVER_MODELS = new Map([
  [
    vidPidKey(DARMOSHARK_VENDOR_ID, DARMOSHARK_RECEIVER_PRODUCT_ID),
    Object.freeze({
      id: 'darmoshark-generic-receiver',
      name: 'DarmoShark 通用接收器',
      vendorId: DARMOSHARK_VENDOR_ID,
      productId: DARMOSHARK_RECEIVER_PRODUCT_ID,
      generic: true,
    }),
  ],
  [
    vidPidKey(DARMOSHARK_VENDOR_ID, LM20_DONGLE_RECEIVER_PRODUCT_ID),
    Object.freeze({
      // NRF54LM20 Dongle：配 MS805 这类 4.0 机型，不复用通用接收器身份。
      id: 'lm20-dongle-receiver',
      name: 'NRF54LM20 Dongle',
      vendorId: DARMOSHARK_VENDOR_ID,
      productId: LM20_DONGLE_RECEIVER_PRODUCT_ID,
      generic: false,
    }),
  ],
  [
    vidPidKey(DARMOSHARK_VENDOR_ID, WANI_8K_RECEIVER_PRODUCT_ID),
    Object.freeze({
      // 配 G1 / H2，不并入通用接收器身份。
      id: 'wani-8k-receiver',
      name: 'wani 8K Receiver',
      vendorId: DARMOSHARK_VENDOR_ID,
      productId: WANI_8K_RECEIVER_PRODUCT_ID,
      generic: false,
    }),
  ],
]);

function normalizeReportedName(name) {
  return String(name || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function registeredBluetoothNames(model) {
  return [model?.bluetoothName, model?.bluetoothPairingName]
    .map((name) => normalizeReportedName(name))
    .filter(Boolean);
}

/**
 * 同一 PID 在有线和蓝牙下上报不同产品名。
 * 与登记的蓝牙名或蓝牙配对名一致时视为蓝牙，其余情况用有线名称。
 */
export function isBluetoothReportedName(model, reportedName = '') {
  const reported = normalizeReportedName(reportedName);
  if (!reported) return false;
  return registeredBluetoothNames(model).includes(reported);
}

export function resolveModelDisplayName(model, reportedName = '') {
  if (!model) return String(reportedName || '');
  if (isBluetoothReportedName(model, reportedName)) {
    return model.bluetoothName || model.bluetoothPairingName;
  }
  return model.name;
}

export function formatConnectionLabel(connectionType) {
  if (connectionType === 'wireless') return '2.4G无线';
  if (connectionType === 'bluetooth') return '蓝牙';
  return '有线';
}

/** 添加无线接收器但鼠标未上线时的连接失败提示。卡片状态用更短的文案。 */
export const WIRELESS_DEVICE_OFFLINE_MESSAGE =
  '无线设备未连接，请开启鼠标并切换到 2.4G 模式';

export const WIRELESS_DEVICE_OFFLINE_LABEL = '无线设备未连接';

export function getDeviceModel(vendorId, productId) {
  return DEVICE_MODELS.get(vidPidKey(vendorId, productId)) || null;
}

/**
 * V4.0 以下的 M9/M6 Ultra 将 DPI 档位颜色放在 command=5/type=1 中。
 */
export function isLegacyDpiColorModel(model) {
  return LEGACY_DPI_COLOR_MODEL_IDS.has(model?.id);
}

export function getReceiverModel(vendorId, productId) {
  return RECEIVER_MODELS.get(vidPidKey(vendorId, productId)) || null;
}

export function isReceiverTransport(vendorId, productId) {
  return getReceiverModel(vendorId, productId) != null;
}

/**
 * 直连 HID（有线或蓝牙）已出现在授权设备列表时，传输和目标设备都视为在线；
 * 型号是否受支持只决定能否进入驱动，不再混用为在线状态。
 * 上报名与登记的蓝牙名一致时按蓝牙显示，否则用有线名称。
 */
export function getWiredDeviceCardState(
  vendorId,
  productId,
  fallbackName = ''
) {
  const model = getDeviceModel(vendorId, productId);
  const supported = Boolean(model);
  const bluetooth = isBluetoothReportedName(model, fallbackName);
  const productName = model
    ? resolveModelDisplayName(model, fallbackName)
    : fallbackName || '未知有线设备';
  return {
    productName,
    image: model?.image || null,
    targetInfo: {
      modelId: model?.id || null,
      productName,
      image: model?.image || null,
      vendorId,
      productId,
    },
    targetOnline: true,
    targetVendorId: vendorId,
    targetProductId: productId,
    accessible: supported,
    connectionKind: bluetooth ? 'bluetooth' : 'wired',
    statusKey: supported ? 'online' : 'unsupported',
    statusLabel: bluetooth ? '蓝牙 · 已连接' : '有线 · 已连接',
    statusMessage: supported
      ? '设备已连接'
      : '设备已连接，但当前驱动不支持此型号',
  };
}

/**
 * 将接收器状态合并为设备卡片状态。
 * receiverInfo 始终描述 USB 接收器；targetInfo 只在目标设备在线时存在。
 */
export function mergeReceiverCardState(entry, status, timedOut = false) {
  const receiver =
    entry?.receiverInfo ||
    getReceiverModel(entry?.vendorId, entry?.productId) ||
    null;
  const presentation = getReceiverStatePresentation(
    status?.deviceState ?? null,
    timedOut
  );
  const targetOnline = status?.deviceState === 1;
  const targetModel = targetOnline
    ? getDeviceModel(status.targetVendorId, status.targetProductId)
    : null;
  const targetInfo = targetOnline
    ? {
        modelId: targetModel?.id || null,
        productName: targetModel?.name || '未支持的无线设备',
        image: targetModel?.image || null,
        vendorId: status?.targetVendorId ?? null,
        productId: status?.targetProductId ?? null,
      }
    : null;

  return {
    ...entry,
    receiverInfo: receiver,
    targetInfo,
    productName:
      targetInfo?.productName || receiver?.name || 'DarmoShark 通用接收器',
    image: targetInfo?.image || null,
    receiverState: status?.deviceState ?? null,
    receiverTimedOut: timedOut,
    reportRateMax: status?.reportRateMax ?? null,
    targetOnline,
    targetVendorId: targetInfo?.vendorId ?? null,
    targetProductId: targetInfo?.productId ?? null,
    accessible: targetOnline && Boolean(targetModel),
    statusKey: presentation.key,
    statusLabel: presentation.label,
    statusMessage: targetModel
      ? presentation.message
      : targetOnline
        ? '已连接设备尚未被当前驱动支持'
        : presentation.message,
  };
}

/**
 * Dongle 状态包 byte6-9 是 UInt32LE：低 16 位 VID，高 16 位 PID。
 */
export function parseDeviceVidPid(data, offset = 6) {
  if (!data || data.length < offset + 4) return null;
  const raw =
    (data[offset] |
      (data[offset + 1] << 8) |
      (data[offset + 2] << 16) |
      (data[offset + 3] << 24)) >>>
    0;
  return {
    raw,
    vendorId: raw & 0xffff,
    productId: (raw >>> 16) & 0xffff,
  };
}

export function getReceiverStatePresentation(state, timedOut = false) {
  if (timedOut) {
    return {
      key: 'timeout',
      label: '检测超时',
      message: '无法获取接收器状态，请重新插拔或重试',
      accessible: false,
    };
  }
  switch (state) {
    case 0:
      return {
        key: 'offline',
        label: WIRELESS_DEVICE_OFFLINE_LABEL,
        message: WIRELESS_DEVICE_OFFLINE_MESSAGE,
        accessible: false,
      };
    case 1:
      return {
        key: 'online',
        label: '无线 · 已连接',
        message: '对应无线设备已连接',
        accessible: true,
      };
    case 2:
      return {
        key: 'mcu-error',
        label: '通信异常',
        message: '接收器通信异常（MCU2 异常）',
        accessible: false,
      };
    case 3:
      return {
        key: 'unpaired',
        label: '未配对',
        message: '接收器尚未配对设备',
        accessible: false,
      };
    default:
      return {
        key: 'checking',
        label: '检测中',
        message: '正在检测接收器设备…',
        accessible: false,
      };
  }
}
